import { useState, type FormEvent } from "react";
import { Button, DataTable, Input, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createBranch, listBranches, updateBranch } from "../../api/branchesApi";
import type { BranchOut } from "../../api/types";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

/**
 * Branch management — `GET/POST /branches`, `PATCH /branches/{id}` (§10.1). OWNER-only,
 * per the existing role-guard pattern (`roles/permissions.ts`'s `ROLES_WITH_BRANCH_ACCESS`
 * and `App.tsx`'s `<RequireRole>` on `/branches`). This is the fix for the most severe
 * business-owner-walkthrough gap: previously a freshly bootstrapped tenant's owner had
 * `assigned_branch_id=None` with no route to ever create a second branch or fix the
 * assignment at all.
 */
export function BranchesPage() {
  const { ensureFreshToken } = useAuth();
  const branchesState = useAsyncData(() => ensureFreshToken().then((token) => listBranches(token)), []);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createBranch(token, { name, location_address: address || undefined });
      setName("");
      setAddress("");
      branchesState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create branch.");
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit(branch: BranchOut) {
    setEditingId(branch.id);
    setEditName(branch.name);
    setEditAddress(branch.location_address ?? "");
    setEditActive(branch.is_active);
    setEditError(null);
  }

  async function handleSaveEdit(event: FormEvent) {
    event.preventDefault();
    if (!editingId) return;
    setEditError(null);
    setEditSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await updateBranch(token, editingId, {
        name: editName,
        location_address: editAddress || null,
        is_active: editActive,
      });
      setEditingId(null);
      branchesState.reload();
    } catch (error) {
      setEditError(error instanceof ApiError ? error.message : "Could not save branch.");
    } finally {
      setEditSubmitting(false);
    }
  }

  const columns: DataTableColumn<BranchOut, keyof BranchOut>[] = [
    { key: "name", header: "Name", render: (r) => r.name },
    { key: "location_address", header: "Address", render: (r) => r.location_address ?? "—" },
    {
      key: "is_active",
      header: "Status",
      render: (r) => <span className={`badge ${r.is_active ? "badge--current" : "badge--overdue"}`}>{r.is_active ? "Active" : "Inactive"}</span>,
    },
    {
      key: "id",
      header: "",
      render: (r) => (
        <Button variant="ghost" size="md" type="button" onClick={() => startEdit(r)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Branches</h1>
          <p className="page__subtitle">Every real branch picker in this app (inventory, staff, purchase orders, transfers) is sourced from this list.</p>
        </div>
      </div>

      {branchesState.error ? <StatusBanner kind="error">{branchesState.error}</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">New branch</h2>
        <form onSubmit={handleCreate}>
          <div className="form-grid">
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <Input label="Address" value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create branch"}
            </Button>
          </div>
        </form>
      </div>

      {editingId ? (
        <div className="panel">
          <h2 className="panel__title">Edit branch</h2>
          <form onSubmit={handleSaveEdit}>
            <div className="form-grid">
              <Input label="Name" value={editName} onChange={(e) => setEditName(e.target.value)} required />
              <Input label="Address" value={editAddress} onChange={(e) => setEditAddress(e.target.value)} />
              <label className="sl-field">
                <span className="sl-field__label">Active</span>
                <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />
              </label>
            </div>
            {editError ? <StatusBanner kind="error">{editError}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={editSubmitting}>
                {editSubmitting ? "Saving…" : "Save"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setEditingId(null)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="panel">
        {branchesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={branchesState.data ?? []}
            getRowId={(row) => row.id}
            emptyMessage="No branches yet."
            caption="Branches"
          />
        )}
      </div>
    </div>
  );
}
